import {Button, Drawer, message} from 'antd';
import {isEditable} from 'common/carriable';
import {
    FlightEditorActionContext,
    FlightEditorAssignmentContext,
    FlightEditorConfiguration,
    isFeatureAuthorized,
    isFeatureEnabled
} from 'components/flighteditor/unified/configuration';
import {
    PrintoutDropdown
} from 'components/flighteditor/unified/UnifiedFlightEditor';
import PaxCgoSearch, {
    initialSearchState,
    initialSelectState,
    SearchState,
    SelectState
} from 'components/search/PaxCgoSearch';
import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';
import {useMutation} from 'react-apollo';
import useUserGroups from 'hooks/useUserGroups';
import useApolloClientHelper from 'hooks/useApolloClientHelper';
import './LiveFlightEditor.less';

const EDIT_LIVE_STATE = gql`
mutation UnifiedFlightEditor_EditLiveState($input: FlightEditCommandInput!, $orgID: ID!) {
    editFlight(input: $input, orgID: $orgID) {
        _id
        state
        liveEditedState
    }
}
`;

const SET_LOCKED_STATE = gql`
mutation UnifiedFlightEditor_SetLockedState($flightID: ID!, $locked: Boolean!) {
    setFlightLocked(flightID: $flightID, locked: $locked) {
        _id
        state
        liveEditedState
    }
}
`;

const LIVE_ASSIGNMENT_POLL_INTERVAL = 10_000;

const LiveAssignmentDrawer: React.FC<FlightEditorAssignmentContext> = ({
    visible,
    loading,
    flight,
    onClose,
    onAssign
}) => {
    const [selectedEntities, setSelectedEntities] = useState<SelectState>(initialSelectState);
    const [searchState, setSearchState] = useState<SearchState>(initialSearchState);
    const [assigning, setAssigning] = useState(false);
    const {getScheduledPaxByIds, getScheduledCgoByIds} = useApolloClientHelper();
    const selectedCount = selectedEntities.pax.length + selectedEntities.cgo.length;

    useEffect(() => {
        if (!visible) {
            setSelectedEntities(initialSelectState);
            setSearchState(initialSearchState);
        }
    }, [visible]);

    const assign = async () => {
        setAssigning(true);
        try {
            const [paxnodes, cgonodes] = await Promise.all([
                getScheduledPaxByIds(selectedEntities.pax),
                getScheduledCgoByIds(selectedEntities.cgo)
            ]);
            await onAssign(paxnodes, cgonodes);
            setSelectedEntities(initialSelectState);
            setSearchState(initialSearchState);
        } catch (error) {
            console.error(error);
            message.error('Failed to assign PAX/CGO');
        } finally {
            setAssigning(false);
        }
    };

    return (
        <Drawer
            visible={visible}
            closable
            destroyOnClose
            width="65%"
            onClose={onClose}
            title={`Assign PAX/CGO to ${flight?.desig || 'flight'}`}
            bodyStyle={{display: 'flex', flexDirection: 'column'}}
            style={{display: 'flex', flexDirection: 'column'}}
            className="ant-drawer-flex ant-drawer-body-no-padding live-flight-assignment-drawer"
        >
            <PaxCgoSearch
                style={{flex: 1, minHeight: 0}}
                selectedEntities={selectedEntities}
                onEntitySelect={setSelectedEntities}
                searchState={searchState}
                onSearchStateChange={setSearchState}
                paxSearchOptions={{
                    filter: {onlyShowLive: true},
                    pollInterval: visible ? LIVE_ASSIGNMENT_POLL_INTERVAL : 0
                }}
                cgoSearchOptions={{
                    filter: {onlyShowLive: true, groundPickup: false},
                    pollInterval: visible ? LIVE_ASSIGNMENT_POLL_INTERVAL : 0
                }}
                tabsProps={{
                    tabBarExtraContent: (
                        <Button
                            type="primary"
                            disabled={loading || assigning || selectedCount === 0}
                            loading={assigning}
                            onClick={assign}
                        >
                            Assign {selectedCount ? `(${selectedCount})` : ''}
                        </Button>
                    )
                }}
            />
        </Drawer>
    );
};

const LivePrintoutAction: React.FC<FlightEditorActionContext> = ({flight, loading}) => (
    <PrintoutDropdown flight={flight} disabled={loading} includeManifest />
);

const LiveFlightEditorActions: React.FC<FlightEditorActionContext> = ({
    flight,
    loading,
    refetch,
    orgData
}) => {
    const [userGroups] = useUserGroups();
    const [editLiveState, editLiveStateResult] = useMutation(EDIT_LIVE_STATE);
    const [setLockedState, setLockedStateResult] = useMutation(SET_LOCKED_STATE);

    const bless = async () => {
        try {
            await editLiveState({
                variables: {
                    input: {
                        flightID: flight._id,
                        action: 'UPDATE_LIVE_STATE',
                        patch: {
                            _id: flight._id,
                            liveEditedState: 'BLESSED'
                        }
                    },
                    orgID: orgData.getActiveOrgID()
                }
            });
            await refetch();
            message.success('Flight blessed');
        } catch (error) {
            console.error(error);
            message.error('Failed to bless flight');
        }
    };

    const toggleLock = async () => {
        try {
            await setLockedState({
                variables: {
                    flightID: flight._id,
                    locked: flight.state !== 'LOCKED'
                }
            });
            await refetch();
        } catch (error) {
            console.error(error);
            message.error('Failed to set locked status');
        }
    };

    const showBless = isFeatureEnabled(liveFlightEditorConfiguration, 'bless', userGroups)
        && isFeatureAuthorized(liveFlightEditorConfiguration, 'bless', userGroups);
    const showLock = isFeatureEnabled(liveFlightEditorConfiguration, 'lock', userGroups)
        && isFeatureAuthorized(liveFlightEditorConfiguration, 'lock', userGroups);
    const lockLabel = flight.state === 'LOCKED' ? 'Unlock' : 'Lock';
    const lockIcon = flight.state === 'LOCKED' ? 'unlock' : 'lock';

    return <React.Fragment>
        {showBless && isEditable(flight) ? (
            <Button
                type="primary"
                disabled={loading || editLiveStateResult.loading}
                loading={editLiveStateResult.loading}
                onClick={bless}
            >
                Bless Flight
            </Button>
        ) : null}
        {showLock && (flight.state === 'ACTIVE' || flight.state === 'LOCKED') ? (
            <Button
                type="primary"
                icon={lockIcon}
                disabled={loading || setLockedStateResult.loading}
                loading={setLockedStateResult.loading}
                onClick={toggleLock}
                style={{marginLeft: 8}}
            >
                {lockLabel}
            </Button>
        ) : null}
    </React.Fragment>;
};

export const liveFlightEditorConfiguration: FlightEditorConfiguration = {
    // Existing live editing is state-controlled and does not require flytsuite.live.flight.
    canEditFlight: isEditable,
    formFields: [
        'estDepartureTime',
        'desig',
        'aircraftID',
        'contractID',
        'pilotID',
        'copilotID',
        'weatherDelay'
    ],
    entityColumns: [
        'name',
        'employer',
        'weight',
        'bagWeight',
        'bagCount',
        'departure',
        'destination',
        'transitType'
    ],
    features: {
        schedulingSteps: {enabled: false},
        weatherDelay: {enabled: true},
        notesForPilot: {enabled: false},
        delete: {enabled: false},
        printout: {enabled: true},
        notify: {enabled: false},
        makeExpected: {enabled: false},
        bless: {enabled: true},
        lock: {
            enabled: true,
            permission: {anyOf: ['flytsuite.flight.lockandunlock'], unauthorized: 'hide'}
        }
    },
    routes: (flight, loading) => [
        {
            path: '/app/flights',
            title: 'Live Flights',
            breadcrumbName: 'Live Flights'
        },
        {
            path: null,
            breadcrumbName: loading ? 'Loading…' : flight?.desig
        }
    ],
    HeaderActions: LiveFlightEditorActions,
    PrintoutAction: LivePrintoutAction,
    AssignmentDrawer: LiveAssignmentDrawer,
    layoutClassName: 'live-unified-flight-editor',
    layoutStyle: requestedStyle => {
        const layoutStyle = {...(requestedStyle || {})};
        delete layoutStyle.margin;
        return {...layoutStyle, overflow: 'hidden'};
    }
};

export default liveFlightEditorConfiguration;
